import { useId, useState } from 'react';
import type {
  MiceCandidate,
  MiceChosenFlight,
  MiceEventWindowFlight,
  MiceEventWindowNote,
} from '@voll-intelligence/domain';
import type { ParseKeys } from 'i18next';
import { t } from '@web/i18n';
import { Button, Spinner } from '@web/ui/core/atoms';
import { ToneChip } from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';
import { useFlightOptions } from '../hooks/use_flight_options';
import { formatFlights, formatLegTimes, formatMoney, sameFlight } from './need_format';

interface FlightOptionsPanelProps {
  projectId: string;
  needId: string;
  version: number;
  direction: 'OUTWARD' | 'RETURN';
  /** "Volta · 12/11": o sentido como o grupo de cartões o mostra. */
  title: string;
  /** O voo do agente no sentido: a linha dele é marcada e a diferença de preço é contra ele. */
  agent: MiceChosenFlight | null;
  onUse: (flight: MiceEventWindowFlight) => void;
  onClose: () => void;
}

const NOTE_KEY = {
  other_event_airport: 'mice.flightOptions.note.other_event_airport',
  outside_requested_airport: 'mice.flightOptions.note.outside_requested_airport',
  last_resort_airport: 'mice.flightOptions.note.last_resort_airport',
  breaks_event_rules: 'mice.flightOptions.note.breaks_event_rules',
} as const satisfies Record<MiceEventWindowNote, ParseKeys>;

/** A diferença para o voo do agente, pelo preço da busca (o mesmo da lista). */
function PriceDiff({
  flight,
  agent,
}: Readonly<{ flight: MiceCandidate; agent: MiceChosenFlight | null }>) {
  if (agent?.price.currency !== flight.price.currency) return null;
  if (sameFlight(flight, agent)) {
    return <ToneChip tone="brand">{t('mice.flightOptions.agent')}</ToneChip>;
  }
  const diff = flight.price.amount - agent.price.amount;
  const amount = formatMoney({ amount: Math.abs(diff), currency: flight.price.currency });
  return (
    <span
      className={twcn(
        'text-micro tabular-nums',
        diff > 0 ? 'text-tone-amber-ink' : 'text-muted-foreground',
      )}
    >
      {diff > 0 ? `+${amount}` : `−${amount}`}
    </span>
  );
}

function OptionRow({
  flight,
  agent,
  name,
  checked,
  onCheck,
}: Readonly<{
  flight: MiceEventWindowFlight;
  agent: MiceChosenFlight | null;
  name: string;
  checked: boolean;
  onCheck: () => void;
}>) {
  const stops =
    flight.stops === 0 ? t('mice.card.direct') : t('mice.card.stops', { count: flight.stops });
  return (
    <label
      className={twcn(
        'flex cursor-pointer items-center gap-3 border-t border-border-soft px-3 py-2',
        checked ? 'bg-muted' : 'hover:bg-muted',
      )}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onCheck}
        aria-label={`${formatFlights(flight)} ${formatLegTimes(flight)}`}
        className="size-4 cursor-pointer accent-primary"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="tabular-nums text-foreground">
          {formatLegTimes(flight)} <span className="text-micro text-muted-foreground">{stops}</span>
        </span>
        <span className="truncate font-mono text-micro text-muted-foreground">
          {`${formatFlights(flight)} · ${flight.from} → ${flight.to}`}
        </span>
        {flight.notes.length > 0 ? (
          <span className="flex flex-wrap gap-1 pt-0.5">
            {flight.notes.map((note) => (
              <ToneChip key={note} tone="amber">
                {t(NOTE_KEY[note], { reasons: flight.rulesBroken.join('; ') })}
              </ToneChip>
            ))}
          </span>
        ) : null}
      </span>
      <span className="flex flex-col items-end gap-0.5">
        <span className="font-medium tabular-nums text-foreground">
          {formatMoney(flight.price)}
        </span>
        <PriceDiff flight={flight} agent={agent} />
      </span>
    </label>
  );
}

function OptionsBody({
  shown,
  agent,
  checked,
  onCheck,
  direct,
  onDirect,
}: Readonly<{
  /** Os voos visíveis: com "Só diretos", só os sem parada. */
  shown: MiceEventWindowFlight[];
  agent: MiceChosenFlight | null;
  checked: string | null;
  onCheck: (id: string) => void;
  direct: boolean;
  onDirect: () => void;
}>) {
  const name = useId();
  const row = (flight: MiceEventWindowFlight) => (
    <OptionRow
      key={flight.id}
      flight={flight}
      agent={agent}
      name={name}
      checked={checked === flight.id}
      onCheck={() => {
        onCheck(flight.id);
      }}
    />
  );
  const equivalent = shown.filter((f) => f.notes.length === 0);
  const others = shown.filter((f) => f.notes.length > 0);
  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3 pb-2">
        <span className="text-micro text-muted-foreground">
          {t('mice.flightOptions.count', { count: shown.length })}
        </span>
        <Button
          size="xs"
          variant={direct ? 'subtle' : 'ghost'}
          aria-pressed={direct}
          onClick={onDirect}
        >
          {t('mice.flightOptions.directOnly')}
        </Button>
      </div>
      <div role="radiogroup" aria-label={t('mice.flightOptions.listLabel')}>
        {equivalent.map(row)}
        {others.length > 0 ? (
          <div role="group" aria-labelledby={`${name}-others`}>
            <span
              id={`${name}-others`}
              className="block border-t border-border-soft px-3 pb-1 pt-2 text-micro text-muted-foreground"
            >
              {t('mice.flightOptions.others')}
            </span>
            {others.map(row)}
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * A lista da faixa do evento de um sentido: os voos que a busca da versão vigente gravou, do mais
 * barato ao mais caro, com a diferença para o voo do agente; os não equivalentes a ele (outro
 * aeroporto do evento, fora do aeroporto pedido, último recurso) num grupo abaixo, com o motivo. "Usar este voo" marca o escolhido no
 * lugar do cartão do sentido; a cotação acontece ao marcar para emitir.
 */
export function FlightOptionsPanel(props: Readonly<FlightOptionsPanelProps>): React.JSX.Element {
  const { data, isLoading, isError } = useFlightOptions(
    props.projectId,
    props.needId,
    props.version,
    props.direction,
  );
  const [checked, setChecked] = useState<string | null>(null);
  const [direct, setDirect] = useState(false);
  const flights = data?.flights ?? [];
  const shown = direct ? flights.filter((f) => f.stops === 0) : flights;
  // Só o voo visível vale: o marcado que o filtro escondeu não pode ser usado.
  const picked = shown.find((f) => f.id === checked) ?? null;
  return (
    <section
      aria-label={t('mice.flightOptions.title', { leg: props.title })}
      className="flex flex-col overflow-hidden rounded-lg border border-border bg-background"
    >
      <div className="flex flex-col gap-0.5 px-3 pb-2 pt-3">
        <span className="font-medium text-foreground">
          {t('mice.flightOptions.title', { leg: props.title })}
        </span>
        <span className="text-micro text-muted-foreground">{t('mice.flightOptions.subtitle')}</span>
      </div>
      {isLoading ? (
        <span className="flex items-center gap-2 px-3 pb-3 text-meta text-muted-foreground">
          <Spinner /> {t('mice.flightOptions.loading')}
        </span>
      ) : null}
      {isError ? (
        <p className="px-3 pb-3 text-meta text-destructive">{t('mice.flightOptions.error')}</p>
      ) : null}
      {data && flights.length === 0 ? (
        <p className="px-3 pb-3 text-meta text-muted-foreground">{t('mice.flightOptions.empty')}</p>
      ) : null}
      {flights.length > 0 ? (
        <OptionsBody
          shown={shown}
          agent={props.agent}
          checked={checked}
          onCheck={setChecked}
          direct={direct}
          onDirect={() => {
            setDirect((d) => !d);
          }}
        />
      ) : null}
      <div className="flex justify-end gap-2 border-t border-border-soft px-3 py-2">
        <Button size="sm" variant="ghost" onClick={props.onClose}>
          {t('mice.flightOptions.cancel')}
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={!picked}
          onClick={() => {
            if (picked) props.onUse(picked);
          }}
        >
          {t('mice.flightOptions.use')}
        </Button>
      </div>
    </section>
  );
}
